/**
 * @module features/agent-evals/useAgentEvals
 *
 * React Query hooks for the agent eval catalog: the paged list (`GET /org/agent-evals`), one eval by
 * name with its runbook, and its cursor-paged run history (`GET /org/agent-evals/:name/runs`).
 */
import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import type { AgentEvalTemplate } from '@chatsdet/types/agent-eval-template';
import { API_URL } from '../../config';
import { ApiError, toApiError } from '../../lib/api-error';

const PAGE_SIZE = 25;

/** A missing eval or a permission failure will not change on retry, so show it at once. */
const retryUnlessClientError = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 3;

export type AgentEvalListItem = {
  name: string;
  description: string;
  caseCount: number;
  defaultModel: string;
  latestVerdict: 'pass' | 'fail' | null;
  latestPassRate: number | null; // 0..1
  latestPassBar: number | null; // 0..1: the bar the latest run was graded against
  latestRanAt: string | null;
};

type ListPage = {
  items: AgentEvalListItem[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
};

export type AgentEvalDetail = {
  name: string;
  description: string;
  definition: AgentEvalTemplate;
  cases: { key: string; title: string }[];
  capabilities: { id: string; code: string; title: string }[];
};

export type AgentEvalRunListItem = {
  id: string;
  modelAlias: string;
  model: string;
  commitSha: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  verdict: 'pass' | 'fail' | null;
  createdAt: string;
  headline: {
    passRate: number;
    passBar: number; // the bar this run was graded against
    costCents: number | null;
  } | null;
};

type RunPage = { items: AgentEvalRunListItem[]; nextCursor: string | null };

/** Page-numbered evals, loaded one page at a time with "Load more". */
export function useAgentEvals() {
  return useInfiniteQuery<ListPage, Error, InfiniteData<ListPage, number>, unknown[], number>({
    queryKey: ['agent-evals', 'list'],
    retry: retryUnlessClientError,
    initialPageParam: 1,
    getNextPageParam: (last) => (last.pagination.page < last.pagination.totalPages ? last.pagination.page + 1 : undefined),
    queryFn: async ({ pageParam }) => {
      const res = await fetch(`${API_URL}/org/agent-evals?page=${pageParam}&pageSize=${PAGE_SIZE}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load evals');
      const body = (await res.json()) as { data: AgentEvalListItem[]; pagination: ListPage['pagination'] };
      return { items: body.data, pagination: body.pagination };
    },
  });
}

export function useAgentEval(name: string) {
  return useQuery<AgentEvalDetail>({
    queryKey: ['agent-evals', 'detail', name],
    retry: retryUnlessClientError,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/org/agent-evals/${encodeURIComponent(name)}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load eval');
      return ((await res.json()) as { data: AgentEvalDetail }).data;
    },
  });
}

export function useAgentEvalRunbook(name: string, enabled: boolean) {
  return useQuery<string>({
    queryKey: ['agent-evals', 'runbook', name],
    enabled,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/org/agent-evals/${encodeURIComponent(name)}/runbook`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load runbook');
      return res.text();
    },
  });
}

/** Cursor-paged run history, newest first. */
export function useAgentEvalRuns(name: string, enabled: boolean) {
  return useInfiniteQuery<RunPage, Error, InfiniteData<RunPage, string | undefined>, unknown[], string | undefined>({
    queryKey: ['agent-evals', 'runs', name],
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) params.set('cursor', pageParam);
      const res = await fetch(`${API_URL}/org/agent-evals/${encodeURIComponent(name)}/runs?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load eval runs');
      return ((await res.json()) as { data: RunPage }).data;
    },
  });
}
